/**
 * Render-time transforms for post bodies, shared by the public card
 * (apps/app SinglePost), the admin canvas editor's card replica, and the
 * html→blocks migration script — one implementation so all three agree on
 * what a "lead image" is and how legacy bodies are cleaned.
 *
 * Moved from apps/app/app/components/SinglePost/SinglePost.tsx; the behavior
 * is pinned by apps/app/app/components/SinglePost/__tests__/extractCover.test.ts.
 */

// A post's LEAD image is lifted out of the body and rendered as a cropped
// cover above the title (top-feed card anatomy).
//
// "Lead" is deliberately strict: the post must OPEN with a single image alone
// in its own paragraph. A looser rule (first <img> found anywhere) once
// promoted the first logo of a sponsor row into a full-width 16:9 crop — and,
// because it consumed the opening <p> without its closing tag, left an
// orphaned image and a dangling </p> behind. Matching a COMPLETE paragraph
// makes both failures impossible.
const LEAD_IMAGE_BLOCK = /^\s*<p[^>]*>(?:\s|<br\s*\/?>)*<img\b([^>]*)>(?:\s|<br\s*\/?>)*<\/p>/i;
const SRC_ATTR = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

export interface ExtractedCover {
  coverSrc: string | null;
  bodyHtml: string;
}

// Returns { coverSrc, bodyHtml }. No lead image => body is passed through
// untouched and the caller renders without a cover.
export function extractCover(html = ''): ExtractedCover {
  const block = html.match(LEAD_IMAGE_BLOCK);
  if (!block) return { coverSrc: null, bodyHtml: html };

  const src = block[1].match(SRC_ATTR);
  // An <img> with no resolvable src is not a cover — leave it where it is
  // rather than removing it from the body and rendering nothing.
  if (!src) return { coverSrc: null, bodyHtml: html };

  return {
    coverSrc: src[1] ?? src[2],
    bodyHtml: html.slice(block[0].length).trim(),
  };
}

// Cleanup applied to legacy Quill bodies (never persisted by the renderer;
// the migration DOES persist its output so migrated blocks match what the
// public site was already displaying): drop a leading legacy "Posted <date>"
// meta line, collapse empty paragraphs, and strip newline-bearing whitespace
// BETWEEN tags — `.ql-editor` is `white-space: pre-wrap`, so pretty-printed
// template HTML otherwise renders each newline as a blank line (the historic
// "gappy bullets" bug). Inline single spaces (no newline) are preserved.
export function cleanBody(html = ''): string {
  return html
    .replace(/^\s*<p[^>]*>\s*<em>\s*Posted[^<]*<\/em>\s*<\/p>/i, '')
    .replace(/<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/>\s*\n\s*</g, '><')
    .trim();
}

export function slugifyPostTitle(text = ''): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
